import { AuthLayout, OAuthButtons } from '@/components/auth'
import {
  Button,
  Control,
  CheckboxInput,
  EmailInput,
  PasswordInput,
  TextInput,
  useForm,
} from '@tempots/beatui'
import { attr, html, on, Ensure } from '@tempots/dom'
import { Validation } from '@tempots/std'
import { Anchor, NavigationService } from '@tempots/ui'
import { auth } from '@/services/gateway'
import { signupSchema } from '@/models/auth'
import { isApiError } from '@/services/errors'

export function SignupPage() {
  const { controller, submit, submitting } = useForm({
    initialValue: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      terms: false,
    },
    schema: signupSchema,
    onSubmit: async (value) => {
      try {
        await auth.register({
          email: value.email,
          password: value.password,
          firstName: value.firstName,
          lastName: value.lastName,
        })
        NavigationService.navigate(
          `/verify-email-sent?email=${encodeURIComponent(value.email)}`
        )
        return Validation.valid
      } catch (err) {
        const message = isApiError(err)
          ? (err.detail ?? err.title)
          : err instanceof Error
            ? err.message
            : 'Registration failed. Please try again.'
        return Validation.invalid({ message })
      }
    },
  })

  const password = controller.field('password')

  return AuthLayout(
    html.div(
      attr.class('max-w-md w-full space-y-8'),
      // Header
      html.div(
        attr.class('text-center'),
        html.h2(
          attr.class('text-2xl sm:text-3xl font-bold text-gray-900'),
          'Create your account'
        ),
        html.p(
          attr.class('mt-2 text-sm sm:text-base text-gray-600'),
          'Get started with Verts today'
        )
      ),
      // Error message display
      Ensure(controller.error, (error) => {
        const errorValue = error.get()
        const isAccountExists =
          errorValue.toLowerCase().includes('account') &&
          errorValue.toLowerCase().includes('exists')
        const email = controller.field('email').signal.get()

        return html.div(
          attr.class(
            'bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm'
          ),
          error,
          isAccountExists && email
            ? html.div(
                attr.class('mt-2'),
                'Need a new verification email? ',
                Anchor(
                  {
                    href: `/verify-email-sent?email=${encodeURIComponent(email)}`,
                  },
                  attr.class(
                    'font-medium text-red-800 underline hover:text-red-900'
                  ),
                  'Resend verification'
                )
              )
            : null
        )
      }),
      // Form
      html.form(
        on.submit((e) => {
          e.preventDefault()
          submit(e)
        }),
        attr.class('space-y-6'),
        // Name fields
        html.div(
          attr.class('grid grid-cols-1 sm:grid-cols-2 gap-4'),
          html.div(
            Control(TextInput, {
              controller: controller.field('firstName'),
              label: 'First name',
              placeholder: 'John',
            })
          ),
          html.div(
            Control(TextInput, {
              controller: controller.field('lastName'),
              label: 'Last name',
              placeholder: 'Doe',
            })
          )
        ),
        // Email
        html.div(
          Control(EmailInput, {
            controller: controller.field('email'),
            label: 'Email Address',
            placeholder: 'you@company.com',
          })
        ),
        // Password
        html.div(
          Control(PasswordInput, {
            controller: password,
            label: 'Password',
          })
        ),
        // Terms checkbox
        html.div(
          Control(CheckboxInput, {
            layout: 'horizontal',
            controller: controller.field('terms'),
            label: html.span(
              'I agree to the ',
              Anchor(
                { href: '/terms-of-service' },
                attr.class('font-medium text-brand-600 hover:text-brand-700'),
                'Terms of Service'
              ),
              ' and ',
              Anchor(
                { href: '/privacy-policy' },
                attr.class('font-medium text-brand-600 hover:text-brand-700'),
                'Privacy Policy'
              )
            ),
          })
        ),
        // Submit button
        html.div(
          attr.class('flex items-center justify-center'),
          Button(
            {
              type: 'submit',
              fullWidth: true,
              loading: submitting,
              disabled: submitting,
            },
            'Create account'
          )
        ),
        OAuthButtons({})
      ),
      // Sign in link
      html.p(
        attr.class('text-center text-sm text-gray-600'),
        'Already have an account? ',
        Anchor(
          { href: '/signin' },
          attr.class('font-medium text-brand-600 hover:text-brand-700'),
          'Sign in'
        )
      )
    )
  )
}
